import type { LocalZone } from "@trip/contracts/bridge";
import type { CSSProperties } from "react";
import { LEVEL_COLORS } from "../lib/danger-sectors.ts";
import { hourLabel, MODE_WORDS } from "./DangerLayer.tsx";

/**
 * The phone's danger legend: the six level colours in a row across the top of the Safety map, and
 * a pointer on the level of the zone the traveller is standing in, as the phone's own zone watcher
 * reads it. The watcher is what buzzes, so the compass, the notifications and the colours agree.
 *
 * Outside every zone, or with no reading at all, the strip goes grey and there is no pointer: a
 * pointer it cannot justify is a guess, and a guess on a safety map reads as a fact.
 *
 * A tap opens the zone's panel; outside every zone there is no zone to open, and it does nothing.
 */
export function DangerCompass({
  zone,
  hour,
  onOpen,
}: {
  zone: LocalZone | null;
  /** The clock hour the colours are for: the page's, the same one the zones are painted with. */
  hour: number;
  onOpen: (sector: string) => void;
}) {
  const sector = zone?.sector ?? null;
  const level =
    sector !== null && zone?.level != null && zone.level >= 1 && zone.level <= 6
      ? Math.round(zone.level)
      : null;
  const known = level !== null;
  return (
    <button
      type="button"
      className="danger-compass"
      aria-label="Danger where you are"
      aria-disabled={known ? undefined : "true"}
      data-none={known ? undefined : "true"}
      onClick={() => {
        if (known && sector !== null) onOpen(sector);
      }}
    >
      <span className="danger-compass-scale">
        {known && (
          <span
            className="danger-compass-pointer"
            data-level={level}
            aria-hidden="true"
            // The middle of its step; the stylesheet slides it there when the zone changes.
            style={{ left: `${((level - 0.5) / 6) * 100}%` }}
          />
        )}
        <span className="danger-compass-strip">
          {LEVEL_COLORS.map((color, i) => (
            <span
              key={color}
              className="danger-compass-step"
              data-dim={known && i + 1 !== level ? "true" : undefined}
              style={{ "--step": color } as CSSProperties}
            >
              {i + 1}
            </span>
          ))}
        </span>
      </span>
      <span className="danger-compass-text">
        {known
          ? `${sector} · ${level} of 6 · ${MODE_WORDS[zone!.mode]} · ${hourLabel(hour)}`
          : "No danger data here"}
      </span>
    </button>
  );
}
